import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ColumnOptionsService } from '@shared/services/column-options.service';
import { of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../base-component/base.component';
import { ColumnOptionsStateKey } from '@shared-features/models/column-options-state-key.model';
import { ColumnOptionsStatePreference } from '@shared-features/models/column-options-state-preference.model';
import { FilterDialogService } from '@shared/services/filter-dialog.service';
import { ColumnOptionsPreferencesComponent } from '@shared/components/column-options-preferences/column-options-preferences.component';
import { ColumnOptionsState } from '@shared-features/models/column-options-state.model';
import { ColumnOptionsLabels } from '@shared/enums/column-options-labels.enum';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { DrawerType } from '@shared/enums/drawer-type.enum';
import { ShipmentFilterLayout } from '@shared/enums/shipment-filter-layout.enum';
import { ButtonTheme } from '@shared/enums/button-theme.enum';
import { SectionStateStatus } from '@shared/enums/section-state-status.enum';
import { IconType } from '@shared/enums/icon-type.enum';

@Component({
  selector: 'app-column-options',
  templateUrl: './column-options.component.html',
  styleUrls: ['./column-options.component.scss'],
})
export class ColumnOptionsComponent extends BaseComponent implements OnInit {
  /** Host-level default. A list can still switch it off for itself via `hideCustomColumnSettings`. */
  @Input() showCustomColumnSettings = true;

  /** Per-list override, re-read on every state emission so one list cannot latch it for another. */
  private hideCustomForList?: boolean;

  get canShowCustomColumnSettings(): boolean {
    return this.hideCustomForList === undefined ? this.showCustomColumnSettings : !this.hideCustomForList;
  }

  @Output() drawerOpened = new EventEmitter<{ open: true; type: DrawerType }>();
  @Output() drawerClosed = new EventEmitter<void>();
  listName: string;
  keys: any;
  state: ColumnOptionsStateKey[];
  defaultState: ColumnOptionsStateKey[];
  /** Mandatory columns for the current list: not removable, not reorderable. Empty for lists that set none. */
  lockedKeys: string[] = [];

  /**
   * Labels to render VERBATIM, keyed by column key.
   *
   * A column listed here skips `'Reports.' + suffix | translate` entirely. That is required for a
   * column whose name is data the user typed - a customer dynamic field - because there is no
   * translation key to look up, so the lookup would render the missing key itself.
   *
   * Absent for every pre-existing list, so they all stay fully translated.
   *
   * Supplied through the column state for the same reason as `lockedKeys`: this component is a
   * single instance in the layout, so there is no per-page binding site.
   */
  rawLabels: Record<string, string> = {};
  /** What this list's Reset restores, when it supplies one. Falls back to `defaultState`. */
  resetState: ColumnOptionsStateKey[];
  preferences: ColumnOptionsStatePreference[];
  selectedPreference: string = null;
  form = new FormGroup({});
  isShown = false;
  destroy$ = new Subject();
  scrollStrategy = this.scrollStrategyOptions.block();
  protected IconType = IconType;

  constructor(
    private scrollStrategyOptions: ScrollStrategyOptions,
    private columnOptionsService: ColumnOptionsService,
    private filterDialogService: FilterDialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.columnOptionsService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.getColumnState(state));

    this.columnOptionsService.drawer$.subscribe((shown: boolean) => {
      if (!shown && this.isShown) {
        this.closeDrawer();
      } else if (shown) {
        this.drawerOpened.emit({ open: true, type: DrawerType.ColumnOptions });
      }
      this.isShown = shown;
    });
  }

  applyChanges(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      return;
    }
    this.columnOptionsService.state = {
      id: this.listName,
      keys: this.keys,
      state: this.state,
      preferences: this.preferences || [],
    };
    this.closeDrawer();
    // Passing the list name lets a list that registered its own persister save through that instead
    // of the shared user-preferences record. Lists without one are unaffected.
    this.columnOptionsService.saveStorage(this.listName).subscribe({
      next: () => this.showSuccessMessage(),
      // Without this the toast fired on `complete` regardless of outcome, so a rejected save still
      // reported "successfully saved".
      error: (error) => this.showErrorMessage(error),
    });
  }

  closeDrawer(): void {
    this.drawerClosed.emit();
  }

  resetOptions(): void {
    const { keys, defaultState, resetState } = this.columnOptionsService.getStateByName(this.listName);
    this.keys = keys;
    // `resetState` is the arrangement a list explicitly nominates for Reset. `defaultState` means
    // something different here - the initial state kept as a fallback - so it is only the fallback.
    this.state = resetState || defaultState || [];
    this.selectedPreference = '';
  }

  setPreference(preference: ColumnOptionsStatePreference): void {
    this.selectedPreference = preference.name;
    this.preferences = this.preferences.map((item) => {
      if (item.name === preference.name) {
        return { ...item, selected: true };
      }
      return { ...item, selected: false };
    });
    this.state = preference.state;
  }

  private async openPreferenceDialog(
    id: string,
    state: ColumnOptionsStateKey[],
    keys: any,
    name: string
  ) {
    const controller = await this.filterDialogService.open(
      ColumnOptionsPreferencesComponent,
      {
        id,
        state,
        name,
        keys,
      },
      {
        filterLayoutType: ShipmentFilterLayout.Nested,
        scrollStrategy: this.scrollStrategy,
        position: 'right',
        title: 'shared.ColumnOptions.CustomTitle',
        buttons: {
          reset: {
            label: ColumnOptionsLabels.BackToOptions,
            theme: ButtonTheme.Flat,
            color: 'primary',
          },
          submit: {
            label: name ? ColumnOptionsLabels.Update : ColumnOptionsLabels.Create,
            theme: ButtonTheme.Raised,
            color: 'secondary',
          },
        },
      }
    );

    controller.dialogRef
      .afterClosed()
      .pipe(switchMap((submit) => (submit ? this.columnOptionsService.saveStorage() : of(null))))
      .subscribe((updated) => {
        if (updated) {
          this.getColumnState(this.columnOptionsService.getStateByName(this.listName));
        }
      });
  }

  openCreatePreferenceDialog(): void {
    this.openPreferenceDialog(this.listName, this.defaultState || [], this.keys, '');
  }

  openEditPreferenceDialog(preference: ColumnOptionsStatePreference): void {
    this.openPreferenceDialog(
      this.listName,
      preference.state || [],
      this.keys,
      preference.name || 'untitled'
    );
  }

  deletePreference(preference: ColumnOptionsStatePreference) {
    const preferences = this.preferences.filter((p) => p.name !== preference.name);
    this.columnOptionsService.state = {
      id: this.listName,
      keys: this.keys,
      state: this.state,
      defaultState: this.defaultState,
      preferences,
    };
    this.sectionState = SectionStateStatus.Loading;
    this.columnOptionsService.saveStorage().subscribe({
      next: () => {
        this.showSuccessMessage();
        this.getColumnState(this.columnOptionsService.getStateByName(this.listName));
        this.sectionState = SectionStateStatus.Ready;
      },
      // Also clears the Loading state, which a failed delete would otherwise leave on screen forever.
      error: (error) => {
        this.showErrorMessage(error);
        this.sectionState = SectionStateStatus.Ready;
      },
    });
  }

  getColumnState({
    id,
    keys,
    state,
    preferences,
    defaultState,
    lockedKeys,
    resetState,
    rawLabels,
    hideCustomColumnSettings,
  }: ColumnOptionsState): void {
    const selectedPreference = this.preferences?.find((p) => p.selected);
    this.listName = id;
    this.keys = keys;
    this.preferences = preferences || [];
    this.state = selectedPreference?.state || state || [];
    this.defaultState = defaultState;
    this.lockedKeys = lockedKeys || [];
    this.rawLabels = rawLabels || {};
    this.hideCustomForList = hideCustomColumnSettings;
    this.resetState = resetState;
    if (selectedPreference) {
      this.selectedPreference = selectedPreference.name;
    }
  }
}
